import { DEFAULT_TEMPLATE_FOR_PRODUCT } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Container, Group, SegmentedControl, Stack, Text, Title } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { DetailSkeleton } from "../../components/PageSkeletons"
import { PipelineGraph } from "../../components/PipelineGraph"
import type { IPipelineGraphEdge, IPipelineGraphNode } from "../../components/PipelineGraph/interfaces"
import { useAuth } from "../../hooks/useAuth"
import { api } from "../../lib/api/client"
import { extractErrorMessage, unwrapData } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import { requireAdmin } from "../../lib/route-guards"
import { detectProduct } from "../../lib/tenant"

interface IPipelineGraphResponse {
  templateId: string
  templates: { id: string; label: string }[]
  waveCount: number
  nodes: IPipelineGraphNode[]
  edges: IPipelineGraphEdge[]
}

function PipelinePage(): React.JSX.Element {
  const { user } = useAuth()
  const { i18n } = useLingui()
  const [template, setTemplate] = useState<string>(DEFAULT_TEMPLATE_FOR_PRODUCT[detectProduct()])

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.admin.pipelineGraph(template),
    queryFn: async (): Promise<IPipelineGraphResponse> => {
      const result = await api.api.admin.pipeline.graph.get({ query: { template } })
      return unwrapData(result) as unknown as IPipelineGraphResponse
    },
    enabled: !!user?.isAdmin,
  })

  useEffect(() => {
    if (!data) return
    if (data.templates.some((item) => item.id === template)) return
    const first = data.templates[0]
    if (first) setTemplate(first.id)
  }, [data, template])

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Stack gap={4}>
            <Title order={2}>
              <Trans>Processing pipeline</Trans>
            </Title>
            <Text size="sm" c="dimmed">
              <Trans>
                The finalize pipeline as a dependency graph. Each node is a processing step; edges show the dependency
                order the executor runs in waves. Pan and zoom to explore.
              </Trans>
            </Text>
          </Stack>
          {data && data.templates.length > 1 && (
            <SegmentedControl
              value={template}
              onChange={setTemplate}
              data={data.templates.map((t) => ({ value: t.id, label: t.label }))}
              aria-label={i18n._(msg`Pipeline template`)}
            />
          )}
        </Group>

        {error && (
          <Alert color="danger" title={<Trans>Could not load the pipeline graph</Trans>}>
            {extractErrorMessage(error)}
          </Alert>
        )}

        {isLoading && !data && <DetailSkeleton />}

        {data && (
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              <Trans>
                {data.nodes.length} steps across {data.waveCount} waves
              </Trans>
            </Text>
            <PipelineGraph nodes={data.nodes} edges={data.edges} waveCount={data.waveCount} />
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

export const Route = createFileRoute("/_auth/pipeline")({
  beforeLoad: requireAdmin,
  component: PipelinePage,
})
